import { useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import { es } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { isoToDate, dateToISO, formatToDDMMYYYY } from '../utils/dateUtils';
import Icon from './icons/IconLibrary';

// Register Spanish locale
registerLocale('es', es);

/**
 * DatePicker Component
 * Enhanced date picker with visual calendar and dd/mm/yyyy format
 */
export default function DatePicker({
  value,
  onChange,
  label = '',
  required = false,
  disabled = false,
  minDate = '',
  maxDate = '',
  className = '',
  error = '',
  placeholder = 'dd/mm/aaaa',
  showQuickOptions = false
}) {
  const [isOpen, setIsOpen] = useState(false);

  // Convert ISO string to Date object for react-datepicker
  const selectedDate = value ? isoToDate(value) : null;
  const minDateObj = minDate ? isoToDate(minDate) : null;
  const maxDateObj = maxDate ? isoToDate(maxDate) : null;

  const handleChange = (date) => {
    if (date) {
      onChange(dateToISO(date));
    } else {
      onChange('');
    }
  };

  // Quick date options (today, yesterday, etc.)
  const quickOptions = showQuickOptions ? [
    { label: 'Hoy', offset: 0 },
    { label: 'Ayer', offset: -1 },
    { label: 'Hace 7 días', offset: -7 },
    { label: 'Hace 30 días', offset: -30 },
  ] : [];

  const handleQuickSelect = (offset) => {
    const date = new Date();
    date.setDate(date.getDate() + offset);
    handleChange(date);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <ReactDatePicker
          selected={selectedDate}
          onChange={handleChange}
          minDate={minDateObj}
          maxDate={maxDateObj}
          disabled={disabled}
          required={required}
          locale="es"
          dateFormat="dd/MM/yyyy"
          placeholderText={placeholder}
          open={isOpen}
          onInputClick={() => !disabled && setIsOpen(true)}
          onClickOutside={() => setIsOpen(false)}
          className={`
            w-full px-3 py-2 pr-10
            border rounded-md
            ${error ? 'border-red-500' : 'border-gray-300 dark:border-slate-600'}
            bg-white dark:bg-slate-800
            text-gray-900 dark:text-gray-100
            placeholder-gray-400 dark:placeholder-gray-500
            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
            disabled:bg-gray-100 dark:disabled:bg-slate-700 disabled:cursor-not-allowed
            ${className}
          `}
          calendarClassName="datepicker-calendar"
          popperClassName="datepicker-popper"
          wrapperClassName="w-full"
        />

        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 pointer-events-none"
          title="Seleccionar fecha"
          tabIndex={-1}
        >
          <Icon name="calendar" className="w-5 h-5" />
        </button>
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {/* Display formatted date */}
      {value && !error && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Fecha seleccionada: {formatToDDMMYYYY(value)}
        </p>
      )}

      {/* Quick Date Options */}
      {showQuickOptions && isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md shadow-lg">
          <div className="py-1">
            {quickOptions.map((option) => (
              <button
                key={option.label}
                type="button"
                onClick={() => handleQuickSelect(option.offset)}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
